import { useMutation, useQuery, useQueryClient, useQueries, UseQueryResult } from "@tanstack/react-query";
import {
  getHomesites,
  patchHomesite,
  getHomeSiteById,
} from "../../../api/homesites/homesites.api";
import { IHomesite } from "../../../api/homesites/homesites.types";
import { IPatchRequest } from "../../../api/common/patch";
import { useCallback, useMemo, useState } from "react";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isBetween from "dayjs/plugin/isBetween";
import dayjs from "dayjs";
import { calculateMetrics } from "../../../utils/homeSiteMetrics/homeSiteMetrics";
import { applyFilters } from "../../../utils/common/searchFilters";
import { HomeSitesDataIndex } from "../../homeSitesDataIndex/types";
import { Communities } from "../../../api/communities/types";

dayjs.extend(isSameOrBefore);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);


export interface CombinedHomeSite {
  data: HomeSitesDataIndex | undefined;
  isLoading: boolean;
  error: unknown;
  isSuccess: boolean;
}

export const useHomeSiteDataById = (divcode: string, homesiteId: string, communityCode: string, communityPhase: string) => {
  const queryClient = useQueryClient();

  const queries = useMemo(
    () => [
      {
        queryKey: ["homeSite", homesiteId, divcode],
        enabled: !!divcode,
        queryFn: () => getHomeSiteById(divcode, homesiteId),
        staleTime: 1000 * 60 * 15, // 15 minutes
        gcTime: 1000 * 60 * 15, // 15 minutes
        refetchInterval: 1000 * 60 * 15, // 15 minutes
      },
    ],
    [divcode, homesiteId]
  );

  const combine = useCallback(
    (idx: UseQueryResult<IHomesite, Error>[]): CombinedHomeSite => {
      const homeSite = idx[0]?.data as IHomesite | undefined;

      // Read communities from cache
      const communities = queryClient.getQueryData<Communities>(["communities", divcode]);
      const communitiesData = communities?.data ?? [];

      const community = communitiesData.find(
        (c) => c.communityCode === communityCode && c.communityPhase === communityPhase
      );

      const data = homeSite
        ? HomeSitesDataIndex.createHomeSiteDataIndex(homeSite, community)
        : undefined;

      return {
        data,
        isLoading: idx.some((r) => r.isPending),
        error: idx.find((r) => r.error)?.error,
        isSuccess: idx.every((r) => r.isSuccess),
      };
    },
    [queryClient, divcode, communityCode, communityPhase]
  );

  const results = useQueries({ queries, combine });
  return results; // CombinedHomeSite
};

export const useUpdateHomeSite = (divcode: string) => {
  const queryClient = useQueryClient();
  return useMutation<IHomesite, Error, { divisionCode: string; homesiteId: string; body: IPatchRequest }>({
    mutationFn: (data) => patchHomesite(data.divisionCode, data.homesiteId, data.body),
    onSuccess: (updatedHomesite, variables) => {
      const { homesiteId } = variables;
      queryClient.setQueryData<IHomesite[]>(
        ["homeSites", divcode],
        (oldData) => {
          if (!oldData) return [];
          const update = oldData.map((homesite) =>
            homesite.homeSiteId === updatedHomesite.homeSiteId
              ? updatedHomesite
              : homesite
          );
          return update;
        }
      );
      queryClient.setQueryData<IHomesite>(
        ["homeSite", homesiteId, divcode],
        (oldData) => {
          if (!oldData) return updatedHomesite;
          return oldData.homeSiteId === updatedHomesite.homeSiteId
            ? updatedHomesite
            : oldData;
        }
      );
    },
    onError: (error) => {
      throw error;
    }
  });
};

export const useHomeSiteObserver = (homeSites: HomeSitesDataIndex[]) => {
  const [filters, setFilters] = useState<Record<string, any>>({});

  const filteredHomesites = useMemo(() => applyFilters(homeSites, filters), [homeSites, filters]);

  const metrics = useMemo(() => calculateMetrics(filteredHomesites, filters), [filteredHomesites, filters]);

  const applyDynamicFilters = useCallback((newFilters: Record<string, any>) => {
    setFilters(newFilters);
  }, []);

  return { metrics, applyDynamicFilters, filteredHomesites, isFetching: !homeSites.length };
};
